'use client'

import { ShoppingCartIcon } from '@heroicons/react/24/outline'
import { Button } from '@/General/Button'
import { MasterclassButton } from '@/MasterclassButton'

type MasterclassButtonsProps = {
  productName: string
  options: {
    id: number
    description: string
    price: number
    salePrice?: number | null
    currencySymbol: string
    inStock: boolean
  }[]
  showOptions: boolean
  showEmailNotification: boolean
  allOptionsOutOfStock: boolean
  isPreorder?: boolean
  noOptionsAvailable: boolean
  storefrontCurrencySymbol?: string | null
  selectedOptionId: number | null
  onSelectedOptionIdChange: (optionId: number) => void
  isDisabled: boolean
  onPurchase: () => void
}

export const MasterclassButtons = ({
  productName,
  options,
  showOptions,
  showEmailNotification,
  allOptionsOutOfStock,
  isPreorder = false,
  noOptionsAvailable,
  storefrontCurrencySymbol,
  selectedOptionId,
  onSelectedOptionIdChange,
  isDisabled,
  onPurchase,
}: MasterclassButtonsProps) => {
  const firstOption = options.length > 0 ? options[0] : null
  const purchasePrice = firstOption?.salePrice ?? firstOption?.price

  const currencySymbol =
    storefrontCurrencySymbol || firstOption?.currencySymbol || '$'

  return (
    <>
      {showOptions && options.length > 1 && (
        <div className="my-2 md:my-3">
          <select
            className="w-full p-2 border border-gray-300 rounded-md"
            onChange={e => onSelectedOptionIdChange(Number(e.target.value))}
            value={selectedOptionId || ''}
          >
            {options
              .filter(option => option?.id)
              .map(option => (
                <option key={option.id} value={option.id || 0}>
                  {option.description}: {option.currencySymbol}
                  {option.price.toFixed(2)}
                  {!option.inStock && !isPreorder ? ' (out of stock)' : ''}
                </option>
              ))}
          </select>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <div className="text-gray-500 text-sm flex-1">
          <MasterclassButton
            variant="product"
            className="w-full text-lg py-4 max-w-md bg-green-500 hover:bg-green-600 text-white"
          />
        </div>

        <div className="text-gray-500 text-sm flex-1">
          <Button
            disabled={isDisabled}
            title={
              showEmailNotification && (allOptionsOutOfStock || noOptionsAvailable)
                ? `Get notified when ${productName} is back in stock`
                : `Add ${productName} to your cart`
            }
            onClick={onPurchase}
            className="w-full text-lg py-4 max-w-md bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
            size="lg"
            color="gray"
          >
            {showEmailNotification && (allOptionsOutOfStock || noOptionsAvailable) ? (
              'Email me when new stock arrives'
            ) : (
              <>
                <ShoppingCartIcon className="w-5 h-5 mr-2" />
                <span className="text-sm">
                  Or purchase just this lecture{' '}
                  {purchasePrice ? `(${currencySymbol}${purchasePrice.toFixed(2)})` : ''}
                </span>
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  )
}


